'use client';

import { Box, OutlinedInput, Typography } from '@mui/material';
import { startTransition } from 'react';
import { useShallow } from 'zustand/shallow';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useFormActionViewedContext } from '@repo/segment/Analytics/FormActionViewed';
import { CALCULATOR_INPUT_HEIGHT_PX } from '../../../shared/CalculatorOutlinedInput';
import { formatToDisplayValue } from '../../../utils/calculatorFormMath';
import { useCdCalculatorStore } from '../calculatorStore';

const MAX_TERM_LENGTH_DIGITS = 3;
const NON_DIGIT_CHARS_REGEX = /\D/g;

function sanitizeTermLengthInput(raw: string): string {
  return raw.replace(NON_DIGIT_CHARS_REGEX, '').slice(0, MAX_TERM_LENGTH_DIGITS);
}

export function TermLengthForm() {
  // TODO: Segment Standardization Cleanup
  const fireCdCalculatorInteractedPageAction = usePageAction({
    actionName: 'cd_calculator_interacted',
    actionSection: 'banking',
    actionType: 'select',
  });
  const { fireFieldInteracted } = useFormActionViewedContext();

  const {
    annualPercentageYield,
    initialDeposit,
    setField,
    termLengthUnit,
    termLength,
    termLengthError,
  } = useCdCalculatorStore(
    useShallow(state => ({
      annualPercentageYield: state.formFields.annualPercentageYield,
      initialDeposit: state.formFields.initialDeposit.includes(',')
        ? state.formFields.initialDeposit.replace(/,/g, '')
        : formatToDisplayValue(state.formFields.initialDeposit),
      setField: state.setField,
      termLengthUnit: state.formFields.termLengthUnit,
      termLength: state.formFields.termLength,
      termLengthError: state.errors.termLength,
    })),
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0, width: '100%' }}
    >
      <Typography component="label" variant="textSmallBold" htmlFor="term-length">
        Term length
      </Typography>
      <OutlinedInput
        id="term-length"
        data-testid="term-length-input"
        fullWidth
        onChange={e => {
          const target = sanitizeTermLengthInput(e.target.value);
          startTransition(() => {
            fireCdCalculatorInteractedPageAction({
              additional_attributes: {
                annual_percentage_yield_value_input: annualPercentageYield,
                initial_deposit_value_input: initialDeposit,
                term_length_value_input: target,
                term_length_unit_value_input: termLengthUnit,
              },
            });
            fireFieldInteracted({
              field: 'term_length_value_input',
              fieldType: 'text',
              fieldValue: target,
              additionalAttributes: {
                annual_percentage_yield_value_input: annualPercentageYield,
                initial_deposit_value_input: initialDeposit,
                term_length_value_input: target,
                term_length_unit_value_input: termLengthUnit,
                page_type: 'roundup',
              },
            });
            setField('termLength', target);
          });
        }}
        slotProps={{
          input: {
            'aria-describedby': termLengthError ? 'term-length-error' : undefined,
            inputMode: 'numeric',
            sx: { padding: 0 },
          },
        }}
        sx={{
          alignItems: 'center',
          borderRadius: '0.5rem',
          boxSizing: 'border-box',
          display: 'flex',
          gap: '.5rem',
          height: CALCULATOR_INPUT_HEIGHT_PX,
          minHeight: CALCULATOR_INPUT_HEIGHT_PX,
          paddingX: '1rem',
          paddingY: 0,
          ...(termLengthError
            ? {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'reds.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'blues.light',
                },
                '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'reds.main',
                },
              }
            : {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'blues.light',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'blues.light',
                },
              }),
        }}
        value={termLength}
      />
      {termLengthError && (
        <Typography id="term-length-error" variant="textExtraSmall" sx={{ color: 'reds.main' }}>
          {termLengthError}
        </Typography>
      )}
    </Box>
  );
}
